(() => {
  const steps = ['application', 'appearance', 'priority'];
  const weights = { appearance: 3, priority: 2 };
  const labels = {
    model: 'Model, maquette of prototype',
    build: 'Onderdeel of bouwpakket',
    sign: 'Display, letters of presentatie',
    inlay: 'Inlay of beschermende verpakking',
    stencil: 'Sjabloon of dunne overlay',
    seal: 'Afdichting of flexibel technisch deel',
    natural: 'Natuurlijke houtuitstraling',
    transparent: 'Helder en transparant',
    color: 'Strak of opvallend gekleurd',
    neutral: 'Eigenschappen zijn belangrijker',
    lightweight: 'Licht van gewicht',
    strong: 'Stevig en stijf',
    flexible: 'Dun of flexibel',
    outdoor: 'Beter passend bij buitengebruik',
    stable: 'Maatvast en voorspelbaar',
    unknown: 'Weet ik nog niet',
  };

  const values = (element, attribute) =>
    (element.dataset[attribute] || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

  const initialize = (root) => {
    if (!root || root.dataset.guideReady === 'true') return;
    root.dataset.guideReady = 'true';

    const questionWrap = root.querySelector('[data-guide-questions]');
    const resultWrap = root.querySelector('[data-guide-results]');
    const materialCards = [...root.querySelectorAll('[data-guide-material]')];
    const progressCurrent = root.querySelector('[data-guide-progress-current]');
    const progressBar = root.querySelector('[data-guide-progress-bar]');
    const backButton = root.querySelector('[data-guide-back]');
    const resetButton = root.querySelector('[data-guide-reset]');
    const summary = root.querySelector('[data-guide-choice-summary]');
    const resultTitle = root.querySelector('[data-guide-result-title]');
    const resultIntro = root.querySelector('[data-guide-result-intro]');
    const answers = {};
    let currentIndex = 0;
    let measurementStarted = false;

    const measure = (eventName, payload = {}) => {
      if (window.LaserLokaalMeasurement) {
        window.LaserLokaalMeasurement.publish(eventName, payload);
      }
    };

    const focusWithoutScroll = (element) => {
      if (!element) return;
      element.setAttribute('tabindex', '-1');
      element.focus({ preventScroll: true });
    };

    const showStep = (index, moveFocus = false) => {
      currentIndex = Math.max(0, Math.min(index, steps.length - 1));
      root.querySelectorAll('[data-guide-step]').forEach((step) => {
        step.hidden = step.dataset.guideStep !== steps[currentIndex];
      });
      backButton.hidden = currentIndex === 0;
      progressCurrent.textContent = String(currentIndex + 1);
      progressBar.style.width = `${((currentIndex + 1) / steps.length) * 100}%`;
      if (moveFocus) focusWithoutScroll(root.querySelector(`[data-guide-step="${steps[currentIndex]}"] legend`));
    };

    const scoreMaterials = () =>
      materialCards
        .map((card) => {
          const applicationMatch =
            answers.application === 'unknown' ||
            values(card, 'application').includes(answers.application);
          let score = 0;
          const matches = [];
          ['appearance', 'priority'].forEach((step) => {
            const answer = answers[step];
            if (answer && answer !== 'unknown' && values(card, step).includes(answer)) {
              score += weights[step];
              matches.push(labels[answer]);
            }
          });
          if (applicationMatch && answers.application !== 'unknown') {
            matches.unshift(labels[answers.application]);
          }
          return {
            card,
            score,
            applicationMatch,
            matches,
            order: Number(card.dataset.order || 999),
          };
        })
        .sort((left, right) => {
          if (left.applicationMatch !== right.applicationMatch) {
            return left.applicationMatch ? -1 : 1;
          }
          return right.score - left.score || left.order - right.order;
        });

    const showResults = () => {
      questionWrap.hidden = true;
      resultWrap.hidden = false;
      root.querySelector('.ll-material-guide__progress').hidden = true;

      summary.innerHTML = steps
        .map((step) => `<span>${labels[answers[step]] || ''}</span>`)
        .join('');

      const hasSpecificAnswer = steps.some(
        (step) => answers[step] && answers[step] !== 'unknown',
      );
      if (resultTitle) {
        resultTitle.textContent = hasSpecificAnswer
          ? 'Deze materialen passen bij je plannen'
          : 'Materialen om mee te beginnen';
      }
      if (resultIntro) {
        resultIntro.textContent = hasSpecificAnswer
          ? 'Deze materialen passen waarschijnlijk goed bij je antwoorden. Open een materiaal voor de eigenschappen en beschikbare diktes.'
          : 'Bekijk deze uiteenlopende materialen en vergelijk hun eigenschappen, uitstraling en beschikbare diktes.';
      }

      scoreMaterials().forEach((result, index) => {
        const visible = index < 4 && result.applicationMatch;
        result.card.hidden = !visible;
        result.card.classList.toggle(
          'is-best-match',
          visible && index === 0,
        );
        if (!visible) return;
        result.card.style.order = String(index);
        result.card.querySelector('[data-guide-match-label]').textContent =
          !hasSpecificAnswer
            ? index === 0 ? 'Begin hier' : 'Ook bekijken'
            : index === 0 ? 'Beste aansluiting' : 'Ook het vergelijken waard';
        result.card.querySelector('[data-guide-reason]').textContent =
          result.matches.length > 0
            ? `Past bij: ${result.matches.join(', ')}.`
            : 'Vergelijk de eigenschappen voor je kiest.';
      });
      focusWithoutScroll(resultWrap.querySelector('h2'));
    };

    root.addEventListener('click', (event) => {
      const answerButton = event.target.closest('[data-guide-answer]');
      if (answerButton) {
        const step = answerButton.closest('[data-guide-step]');
        answers[step.dataset.guideStep] = answerButton.dataset.guideAnswer;
        if (!measurementStarted) {
          measurementStarted = true;
          measure('material_guide_started');
        }
        step.querySelectorAll('[data-guide-answer]').forEach((button) => {
          button.setAttribute(
            'aria-pressed',
            String(button === answerButton),
          );
        });

        if (currentIndex < steps.length - 1) {
          showStep(currentIndex + 1, true);
        } else {
          showResults();
          measure('material_guide_completed', {
            guide_application: answers.application,
            guide_appearance: answers.appearance,
            guide_priority: answers.priority,
          });
        }
        return;
      }

      if (event.target.closest('[data-guide-back]')) {
        showStep(currentIndex - 1, true);
        return;
      }

      if (event.target.closest('[data-guide-reset]')) {
        measurementStarted = false;
        steps.forEach((step) => delete answers[step]);
        root.querySelectorAll('[data-guide-answer]').forEach((button) => {
          button.removeAttribute('aria-pressed');
        });
        materialCards.forEach((card) => {
          card.hidden = true;
          card.classList.remove('is-best-match');
          card.style.removeProperty('order');
        });
        resultWrap.hidden = true;
        questionWrap.hidden = false;
        root.querySelector('.ll-material-guide__progress').hidden = false;
        showStep(0, true);
      }
    });

    showStep(0);
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll?.('[data-ll-material-guide]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();
